/**
 * Helpers for the BrandKit "Company Dictionary" (`customVocabulary`) field.
 *
 * The backend builds the transcription `keyterms_prompt` from this value and
 * expects a comma-separated list of terms. Newline-separated input is read as a
 * single very long keyterm and breaks transcription, so we validate it client
 * side before saving.
 */

const LINE_BREAK = /\r?\n/;

/** True when the value contains any line break. */
export function containsLineBreaks(value: string): boolean {
  return LINE_BREAK.test(value);
}

/**
 * Collapses a newline- (or mixed comma/newline-) separated list into a clean,
 * comma-separated single line. Trims each line, strips any trailing comma from
 * a line so we never produce a double comma, and drops empty lines.
 */
export function convertLineBreaksToCommas(value: string): string {
  return value
    .split(LINE_BREAK)
    .map(line => line.trim().replace(/,\s*$/, '').trim())
    .filter(line => line.length > 0)
    .join(', ');
}

/**
 * Appends a single term to a comma-separated vocabulary string, keeping the
 * comma-separated contract intact.
 *
 * Line breaks are the failure mode the backend can't tolerate (a newline-
 * separated value is read as one giant keyterm), so both sides are sanitized:
 * the incoming term has any line breaks and commas collapsed to spaces so it
 * stays a single valid entry, and line breaks already present in `vocabulary`
 * are converted to commas before appending. De-duplicates case-insensitively
 * and returns the original string unchanged when the term is empty (after
 * sanitizing) or already present.
 */
export function addTermToVocabulary(vocabulary: string, term: string): string {
  const sanitizedTerm = term
    .replace(/[\r\n,]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!sanitizedTerm) return vocabulary;

  const existing = convertLineBreaksToCommas(vocabulary)
    .split(',')
    .map(entry => entry.trim())
    .filter(entry => entry.length > 0);

  const alreadyPresent = existing.some(
    entry => entry.toLowerCase() === sanitizedTerm.toLowerCase(),
  );
  if (alreadyPresent) return vocabulary;

  return [...existing, sanitizedTerm].join(', ');
}
